import React, { forwardRef, useState } from 'react';
import { CustomModal } from '@components';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiGetTtfSetPriceProfile, apiSaveTtfPriceProfile } from '@store';
import { useDispatch } from 'react-redux';
import { showToast } from '@utils';

const CreateProfile = forwardRef(
    ({ handleClose, eur, chf, user, market = 'TTF' }, ref) => {
        const dispatch = useDispatch();
        const contract = eur?.symbols;
        const chfContract = chf?.symbols;

        const [state, setState] = useState({
            name: '',
            currency: '',
            product: market,
            user: user,
            contracts: [],
            contractvolume: {}
        });

        const handleChange = (e) => {
            const { name, value } = e.target;
            setState((prev) => ({ ...prev, [name]: value }));
        };

        const handleContractSelect = (index, value) => {
            const newContracts = [...state.contracts];
            newContracts[index] = value;
            setState((prev) => ({ ...prev, contracts: newContracts }));
        };

        const handleAddContract = () => {
            setState((prev) => ({ ...prev, contracts: [...prev.contracts, ''] }));
        };

        const handleRemoveContract = (index) => {
            const contractToRemove = state.contracts[index];
            const newContractVolume = { ...state.contractvolume };
            delete newContractVolume[contractToRemove];

            setState((prev) => ({
                ...prev,
                contracts: prev.contracts.filter((_, i) => i !== index),
                contractvolume: newContractVolume
            }));
        };

        const handleContractVolumeChange = (e) => {
            const { name, value } = e.target;
            setState((prev) => ({
                ...prev,
                contractvolume: { ...prev.contractvolume, [name]: value }
            }));
        };

        const getAvailableContracts = (currentIndex) => {
            const allContracts = state.currency === 'eur' ? contract : chfContract;
            const selectedContracts = state.contracts.filter(
                (c, i) => i !== currentIndex && c !== ''
            );
            return allContracts?.filter((c) => !selectedContracts.includes(c)) || [];
        };

        const handleSubmit = async (e) => {
            e.preventDefault();

            // Filter out the contractvolume that are no in contracts
            const tempContractVolume = Object.keys(state.contractvolume).reduce(
                (acc, key) => {
                    if (state.contracts.includes(key)) {
                        acc[key] = state.contractvolume[key];
                    }
                    return acc;
                },
                {}
            );

            // convert contractvolume to float32 or float64
            Object.keys(tempContractVolume).forEach((key) => {
                tempContractVolume[key] = parseFloat(tempContractVolume[key]);
            });

            const data = {
                name: state.name,
                currency: state.currency,
                product: state.product,
                user: state.user,
                contractvolume: tempContractVolume
            };

            const resp = await dispatch(apiSaveTtfPriceProfile(data));
            if (resp?.meta?.requestStatus === 'fulfilled') {
                handleClose();
                dispatch(apiGetTtfSetPriceProfile());
                showToast('success', 'TTF Profile created successfully');
            } else if (resp?.meta?.requestStatus === 'rejected') {
                showToast(
                    'error',
                    resp.payload || 'Failed to create TTF profile'
                );
            }
        };

        return (
            <CustomModal
                handleClose={handleClose}
                modalTitle={`Create TTF Profile`}
                handleSubmit={handleSubmit}
                buttonText={'Submit Profile'}
                ref={ref}
            >
                <Box className="">
                    <Box className="flex gap-2 mb-4">
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="profileNameLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Profile Name
                            </InputLabel>
                            <OutlinedInput
                                id="name"
                                label="Profile Name"
                                name="name"
                                onChange={handleChange}
                                value={state.name}
                                required
                            />
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">
                                Currency
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Currency"
                                name="currency"
                                onChange={handleChange}
                                value={state.currency}
                                required
                            >
                                <MenuItem value="chf">CHF</MenuItem>
                                <MenuItem value="eur">EUR</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    {/* Selected contracts with volume inputs */}
                    <Box className="mt-4 flex flex-col gap-3 max-h-96 overflow-y-auto px-2">
                        {state.contracts.map((selectedContract, index) => {
                            const availableContracts = getAvailableContracts(index);

                            return (
                                <Box key={index} className="flex items-start gap-2">
                                    <FormControl sx={{ minWidth: 180 }}>
                                        <InputLabel id={`contract-label-${index}`}>
                                            Contract
                                        </InputLabel>
                                        <Select
                                            labelId={`contract-label-${index}`}
                                            value={selectedContract}
                                            label="Contract"
                                            onChange={(e) => handleContractSelect(index, e.target.value)}
                                            required
                                        >
                                            {selectedContract && (
                                                <MenuItem value={selectedContract}>
                                                    {selectedContract}
                                                </MenuItem>
                                            )}
                                            {availableContracts.map((name) => (
                                                <MenuItem key={name} value={name}>
                                                    {name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel
                                            id={`volume-label-${index}`}
                                            sx={{
                                                bgcolor: '#F5F5F5',
                                                p: '0 3px'
                                            }}
                                        >
                                            Volume
                                        </InputLabel>
                                        <OutlinedInput
                                            id={`volume-${index}`}
                                            label="Volume"
                                            name={selectedContract}
                                            value={state.contractvolume[selectedContract] || ''}
                                            onChange={handleContractVolumeChange}
                                            type="number"
                                            inputProps={{ step: 0.01, min: 0 }}
                                            required
                                            disabled={!selectedContract}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    MW
                                                </InputAdornment>
                                            }
                                        />
                                    </FormControl>
                                    <IconButton
                                        onClick={() => handleRemoveContract(index)}
                                        color="error"
                                        aria-label="remove contract"
                                        sx={{ mt: 1 }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            );
                        })}
                        {state.currency && getAvailableContracts(state.contracts.length).length > 0 && (
                            <Button
                                variant="outlined"
                                onClick={handleAddContract}
                                size="small"
                                sx={{ mt: 1, alignSelf: 'flex-start' }}
                            >
                                {state.contracts.length === 0 ? 'Add contract' : 'Add another contract'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </CustomModal>
        );
    }
);

export default CreateProfile;
